import {
  getLocalTime,
  getUTCDateFromTime,
  isAfterDay,
} from '@gripp/shared-logic';
import { useCallback, useLayoutEffect, useState } from 'react';
import { useController, useWatch } from 'react-hook-form';
import { View } from 'react-native';
import { Text } from 'react-native-paper';
import TimePicker from 'react-time-picker';
import 'react-time-picker/dist/TimePicker.css';
import './webDateTimePicker.css';

import { Icon } from '../../crop-circle/icons';
import { tokens } from '../../crop-circle/tokens';
import { useFormContext } from '../formContext';
import { datePickerStyle } from './datePickerStyle';
import { DateRangeSelectorProps } from './dateRangeSelector';

const isSafari =
  typeof navigator !== 'undefined' &&
  /Safari/.test(navigator.userAgent) &&
  !/Chrome/.test(navigator.userAgent);

type WebDateTimePickerProps = DateRangeSelectorProps & {
  name: string;
  isStartDate?: boolean;
  label?: string;
};

export const WebDateTimePicker = ({
  isStartDate = false,
  name,
  ...props
}: WebDateTimePickerProps) => {
  const {
    field: { value },
  } = useController({ name, control: props.control });
  const rangeItem = useWatch({
    control: props.control,
    name,
  });

  const [timeValue, setTimeValue] = useState<string | null>(
    value ? getLocalTime(value) : null
  );

  const { getValues } = useFormContext();

  const updateTimeValue = useCallback(
    (fieldName: string, val: string) => {
      props.setValue(fieldName, val, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [props]
  );

  const handleChangeTime = useCallback(
    (time: string | null) => {
      setTimeValue(time);
      if (!time) return;
      const timeUTC = getUTCDateFromTime(time);
      if (isStartDate) {
        updateTimeValue(name, timeUTC);
        const endTime = getValues(name.replace('startTime', 'endTime'));
        if (!endTime || isAfterDay(timeUTC, endTime)) {
          updateTimeValue(name.replace('startTime', 'endTime'), timeUTC);
        }
        return;
      }
      if (name.includes('endTime')) {
        const startTime = getValues(name.replace('endTime', 'startTime'));
        if (startTime && isAfterDay(startTime, timeUTC)) {
          return;
        }
      }
      updateTimeValue(name, timeUTC);
    },
    [isStartDate, name, updateTimeValue, getValues]
  );

  useLayoutEffect(() => {
    if (name.includes('endTime')) {
      const localTime = getLocalTime(rangeItem);
      setTimeValue(localTime || null);
    }
  }, [rangeItem]);

  return (
    <View
      style={[
        datePickerStyle.timeContainer,
        { width: isStartDate || name.includes('endTime') ? '48%' : '100%' },
      ]}
    >
      {props.label && (
        <View style={datePickerStyle.labelContainer}>
          <Text style={datePickerStyle.label}>{props.label}</Text>
        </View>
      )}
      {isSafari ? (
        <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}>
          <TimePicker
            onChange={handleChangeTime}
            value={timeValue}
            disableClock
            clearIcon={null}
            clockIcon={null}
            format="hh:mm a"
            className="gripp-time-picker"
          />
          <View style={{ marginLeft: 'auto', paddingRight: 8 }}>
            <Icon name="Clock B" size={18} color={tokens.colors.brand.black} />
          </View>
        </View>
      ) : (
        <View style={{ flex: 1 }}>
          <input
            type="time"
            id={name}
            name={name}
            style={{
              border: 'none',
              WebkitAppearance: 'none',
              MozAppearance: 'textfield',
              backgroundColor: 'transparent',
              outline: 'none',
              fontSize: 16,
              padding: '0 0 0 16px',
              fontFamily: 'Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif',
              fontWeight: 400,
            }}
            onChange={(e) => handleChangeTime(e.target.value || null)}
            value={timeValue ?? ''}
          />
        </View>
      )}
    </View>
  );
};
